/**
 * Cases-on-map data layer.
 *
 * Wraps `getMapCases()` from `./api.ts` with a static-data fallback so the
 * widget keeps rendering when the backend is unreachable (offline dev, CDN
 * outage, etc.). Callers should prefer this helper over the raw API.
 */
import { getMapCases, type MapCasesQuery, type MapDataset } from "./api";
import {
  filterByRadius,
  filterByStatus,
  filterByZip,
  staticDatasetFor,
  type MapStatusFilter,
} from "./cases-data";

export interface FetchMapCasesOptions extends MapCasesQuery {
  /**
   * When true, skip the network entirely and serve the bundled static
   * dataset. Useful for the standalone landing page and for offline dev.
   */
  staticOnly?: boolean;
  /**
   * Status filter applied to whatever source the points came from — API,
   * static, or fallback. This is the official replacement for host pages
   * monkey-patching window.fetch to pre-filter the payload.
   */
  filter?: MapStatusFilter;
}

export interface FetchMapCasesResult {
  dataset: MapDataset;
  /** Where the data actually came from, for logging / debug overlays. */
  source: "api" | "static-fallback" | "static";
}

/**
 * Fetch the map dataset, falling back to the bundled static data on error.
 * Never throws.
 */
export async function fetchMapCases(
  options: FetchMapCasesOptions = {},
): Promise<FetchMapCasesResult> {
  const city = (options.city || "berlin").toLowerCase();

  if (options.staticOnly) {
    return {
      dataset: applyClientFilters(staticDatasetFor(city), options),
      source: "static",
    };
  }

  try {
    const data = await getMapCases({
      city,
      zip: options.zip,
      centerLat: options.centerLat,
      centerLng: options.centerLng,
      radiusKm: options.radiusKm,
    });
    if (data && Array.isArray(data.points)) {
      return { dataset: filterByStatus(data, options.filter), source: "api" };
    }
    // 404 / null — endpoint not deployed yet (or returned an unexpected
    // shape). Log it so this isn't a silent surprise, then fall through
    // to the bundled static dataset below.
    // eslint-disable-next-line no-console
    console.warn(
      "[conny] map cases API returned no usable data (likely 404). " +
        "Falling back to bundled static dataset.",
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      "[conny] map cases API failed, using static fallback:",
      err instanceof Error ? err.message : err,
    );
  }

  const fallback = applyClientFilters(staticDatasetFor(city), options);
  // eslint-disable-next-line no-console
  console.info(
    `[conny] map widget showing ${fallback.points.length} static fallback points for city="${city}"`,
  );
  return { dataset: fallback, source: "static-fallback" };
}

function applyClientFilters(
  dataset: MapDataset,
  options: FetchMapCasesOptions,
): MapDataset {
  let out = filterByStatus(dataset, options.filter);
  if (options.zip) out = filterByZip(out, options.zip);
  if (
    options.centerLat != null &&
    options.centerLng != null &&
    options.radiusKm != null
  ) {
    out = filterByRadius(
      out,
      options.centerLat,
      options.centerLng,
      options.radiusKm,
    );
  }
  return out;
}
